import { API_CONFIG } from '../config/api';

export interface LoginRequest {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface RegisterRequest {
  email: string;
  password: string;
  confirmPassword: string;
}

export interface LoginResponse {
  success: boolean;
  userId: string;
  email: string;
  userName: string;
  roles?: string[];
  redirectUrl?: string; // SSO redirect URL if registration/action required
}

export interface UserDto {
  id: string;
  email: string;
  userName: string;
  roles?: string[];
}

export class AuthClient {
  private baseUrl: string;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || API_CONFIG.baseUrl;
  }

  async login(request: LoginRequest): Promise<LoginResponse> {
    const response = await fetch(`${this.baseUrl}/api/users/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Important for cookies
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Invalid email or password');
      }
      throw new Error('Login failed');
    }

    // Check if response has content (might be redirect response or empty)
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const data = await response.json();

      // Check if SSO returned a redirect URL (registration required, etc.)
      if (data && data.redirectUrl) {
        // Return the redirect URL - let the caller handle the redirect
        return {
          success: false,
          userId: '',
          email: '',
          userName: '',
          roles: [],
          redirectUrl: data.redirectUrl
        };
      }
    }

    // Identity API returns empty response with cookie
    // Get user info from /me endpoint
    if (response.status === 200) {
      const user = await this.getCurrentUser();
      if (user) {
        return {
          success: true,
          userId: user.id,
          email: user.email,
          userName: user.userName,
          roles: user.roles || []
        };
      }
    }

    throw new Error('Login succeeded but failed to get user info');
  }

  async register(request: RegisterRequest): Promise<LoginResponse> {
    const response = await fetch(`${this.baseUrl}/api/users/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.errors?.join(', ') || 'Registration failed');
    }

    // Identity API returns empty response, get user info
    const user = await this.getCurrentUser();
    if (user) {
      return {
        success: true,
        userId: user.id,
        email: user.email,
        userName: user.userName
      };
    }

    throw new Error('Registration succeeded but failed to get user info');
  }

  async logout(): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/users/logout`, {
      method: 'POST',
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Logout failed');
    }
  }

  async getCurrentUser(): Promise<UserDto | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/users/me`, {
        method: 'GET',
        credentials: 'include',
      });

      if (response.status === 401) {
        return null;
      }

      if (!response.ok) {
        throw new Error('Failed to get current user');
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  }
}

export const authClient = new AuthClient();
